/** True when the user is already on this exact nav target (path + query; not parent/child paths). */
export function isSameNavRoute(pathname, to, { end = false, search = '', currentSearch = '' } = {}) {
  if (end || to === '/') {
    return pathname === to && (!search || search === currentSearch);
  }
  if (pathname !== to) return false;
  if (search && search !== currentSearch) return false;
  return true;
}

/** Parse internal link target path + search from href. */
export function parseInternalNavTarget(raw, origin = window.location.origin) {
  try {
    const url = new URL(raw, origin);
    return { pathname: url.pathname, search: url.search };
  } catch {
    const [pathPart, rest = ''] = raw.split('?');
    const path = pathPart.split('#')[0];
    const search = rest ? `?${rest.split('#')[0]}` : '';
    return { pathname: path, search };
  }
}

export function scrollToPageTop({ smooth = false } = {}) {
  const behavior = smooth ? 'smooth' : 'auto';
  window.scrollTo({ top: 0, left: 0, behavior });
  document.documentElement.scrollTop = 0;
  document.body.scrollTop = 0;
}

/** Scroll to top after route change — retries after mobile nav collapse and layout paint. */
export function scheduleScrollToPageTop({ smooth = false } = {}) {
  const run = () => scrollToPageTop({ smooth });

  run();

  const nav = document.getElementById('navbarNav');
  if (nav?.classList.contains('show')) {
    nav.addEventListener('hidden.bs.collapse', run, { once: true });
  }

  let rafId = window.requestAnimationFrame(() => {
    rafId = window.requestAnimationFrame(run);
  });
  const timeoutId = window.setTimeout(run, 180);

  return () => {
    window.cancelAnimationFrame(rafId);
    window.clearTimeout(timeoutId);
    nav?.removeEventListener('hidden.bs.collapse', run);
  };
}

export function isMobileNavOpen() {
  return document.getElementById('navbarNav')?.classList.contains('show') ?? false;
}

/** Close the mobile drawer without toggling (avoids swallowing the link click). */
export function closeMobileNav() {
  const nav = document.getElementById('navbarNav');
  if (!nav?.classList.contains('show')) return;
  const Collapse = typeof window !== 'undefined' ? window.bootstrap?.Collapse : null;
  if (Collapse) {
    Collapse.getOrCreateInstance(nav, { toggle: false }).hide();
    return;
  }
  const toggler = document.querySelector('button.navbar-toggler[data-bs-target="#navbarNav"]');
  toggler?.click();
}

/** Re-tap current nav: close mobile menu + scroll to top (no full reload). Cross-route taps navigate normally. */
export function createSameRouteNavHandler(pathname, { onBefore, smooth = true, currentSearch = '' } = {}) {
  return (to, { end = false, search = '' } = {}) => (e) => {
    if (!isSameNavRoute(pathname, to, { end, search, currentSearch })) return;
    onBefore?.();
    e.preventDefault();
    scheduleScrollToPageTop({ smooth });
  };
}
